import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface InventoryBatch {
  id: string;
  variant_id: string;
  warehouse_id: string;
  quantity: number;
  batch_number: string;
  expiry_date?: string;
}

interface InventoryState {
  byId: Record<string, InventoryBatch>;
  allIds: string[];
  filters: {
    search?: string;
    warehouseId?: string;
  };
  isLoading: boolean;
  error: string | null;
}

const initialState: InventoryState = {
  byId: {},
  allIds: [],
  filters: {},
  isLoading: false,
  error: null,
};

const inventorySlice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {
    setInventoryBatches: (state, action: PayloadAction<InventoryBatch[]>) => {
      state.byId = {};
      state.allIds = [];
      action.payload.forEach((batch) => {
        state.byId[batch.id] = batch;
        state.allIds.push(batch.id);
      });
    },
    addInventoryBatch: (state, action: PayloadAction<InventoryBatch>) => {
      const batch = action.payload;
      state.byId[batch.id] = batch;
      if (!state.allIds.includes(batch.id)) {
        state.allIds.push(batch.id);
      }
    },
    updateInventoryBatch: (state, action: PayloadAction<InventoryBatch>) => {
      const batch = action.payload;
      state.byId[batch.id] = batch;
    },
    deleteInventoryBatch: (state, action: PayloadAction<string>) => {
      delete state.byId[action.payload];
      state.allIds = state.allIds.filter((id) => id !== action.payload);
    },
    setFilters: (state, action: PayloadAction<Partial<InventoryState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setInventoryBatches,
  addInventoryBatch,
  updateInventoryBatch,
  deleteInventoryBatch,
  setFilters,
  setLoading,
  setError,
} = inventorySlice.actions;

export default inventorySlice.reducer;
